import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Info from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';
import ApiIcon from '@mui/icons-material/Api';
import { Tooltip } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { Box } from '@mui/material';
import SalePrint from './Print';
import PrintIcon from '@mui/icons-material/Print';
import ReactDOM from 'react-dom';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

function SaleReturnIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [open, setOpen] = useState(false);
    const [requestedItemdata, setrequestedItemdata] = useState([]);
    const [purchaseRequestdatacount, setpurchaseRequestdatacount] = useState('');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/SaleReturnCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/SaleReturnEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else if (location.state?.from === '/SaleReturnDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    const handleClose = () => {
        setOpen(false);
    };

    // Define the modal style
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%',
        bgcolor: 'white',
        boxShadow: 24,
        borderRadius: '10px'
    };

    useEffect(() => {
        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        // Authentication and token expiration handling
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

        // Other initializations, if any
    }, [isAuthenticated, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);

    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    //search
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    // Function to fetch table data and set state
    const fetchTableData = async () => {
        setData([]);
        try {
            const response = await fetchData('SaleReturnApi', `/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching SaleOrder Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    const handleDelete = async () => {
        deleteData('SaleReturnApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SaleReturnApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const commonButtonStyles = {
        height: '36px',
        marginLeft: '10px',
        marginTop: '10px',
        marginRight: '8px',
    };

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('PurchaseOrderDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortedData = stableSort(filteredData, getComparator(order, orderBy));

    const handlePrint = async (SALEID) => {
        try {
            const response = await fetchData('SaleReturnApi', `/SaleReturnPrint/${SALEID}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <SalePrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    return (
        <div className="w-full mx-auto" style={{ width: "100%", marginTop: "-5px" }}>
            {/* Filter Bar */}
            <div
                className="flex items-center font-serif gap-3 p-2 rounded-xl shadow-sm"
                style={{ background: "#f9fafb", marginBottom: "10px" }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2 text-gray-700 flex-grow"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                >
                    Sale Return
                </h6>

                {/* Search */}
                <input
                    type="text"
                    placeholder="🔍 Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "240px" : "180px",
                        height: "30px",
                        border: "1px solid #ddd",
                        borderRadius: "20px",
                        padding: "0 14px",
                        fontSize: "12px",
                        transition: "all 0.3s ease",
                        outline: "none",
                    }}
                />

                {/* From Date */}
                <input
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* To Date */}
                <input
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    style={{
                        height: "30px",
                        fontSize: "12px",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "0 6px",
                        fontFamily: 'Arial, sans-serif',
                    }}
                />

                {/* Refresh Button */}
                <button
                    onClick={fetchTableData}
                    style={{
                        background: "#1090a3",
                        color: "white",
                        fontSize: "12px",
                        border: "none",
                        borderRadius: "20px",
                        padding: "4px 14px",
                        cursor: "pointer",
                        transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = "#0b6d7a")}
                    onMouseOut={(e) => (e.currentTarget.style.background = "#1090a3")}
                >
                    Refresh
                </button>
            </div>

            {/* Table Section */}
            <div
                className="rounded-lg overflow-hidden shadow-sm"
                style={{ maxHeight: "480px", overflowY: "auto", marginTop: '-10px' }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow style={{ backgroundColor: "#0f172a" }}> {/* Dark navy-blue */}
                                {[
                                    "Sl.No",
                                    "Bill Date",
                                    "Bill No",
                                    "Name",
                                    "Amount",
                                    "CGST",
                                    "SGST",
                                    "IGST",
                                    "Total",
                                    "Qty",
                                    "",
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            borderBottom: "2px solid #1e293b",
                                            color: "#f8fafc", // soft white
                                            padding: "10px 12px",
                                            textAlign: ["Amount", "CGST", "SGST", "IGST", "Total", "Qty"].includes(head)
                                                ? "right"
                                                : "left",
                                            letterSpacing: "0.5px",
                                            backgroundColor: "#0f172a", // Dark navy-blue
                                            padding: '2px'

                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>


                        <TableBody>
                            {sortedData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((data, index) => (
                                    <TableRow
                                        key={data.Id}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                                            transition: "0.2s",
                                            cursor: 'pointer'
                                        }}
                                        hover
                                    >
                                        <TableCell style={{ fontSize: '12px' }}>{index + 1}</TableCell>
                                        <TableCell style={{ fontSize: '12px' }}>{formatDate(data.BillDate)}</TableCell>
                                        <TableCell style={{ fontSize: '12px' }}>
                                            {data.BillPrefix}/{data.BillNumber}/{data.FinYear}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }}>{data.CustomerName}</TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">
                                            {data.TaxableAmount?.toFixed(2) ?? "0.00"}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">
                                            {data.CGSTAmount?.toFixed(2) ?? "0.00"}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">
                                            {data.SGSTAmount?.toFixed(2) ?? "0.00"}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">
                                            {data.IGSTAmount?.toFixed(2) ?? "0.00"}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">
                                            {data.TotalAmount?.toFixed(2) ?? "0.00"}
                                        </TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="right">{data.TotalQuantity}</TableCell>
                                        <TableCell style={{ fontSize: '12px' }} align="center">
                                            <Tooltip title="Sale Return Print" arrow>
                                                <IconButton size="small" onClick={() => handlePrint(data.Id)}>
                                                    <PrintIcon fontSize="small" style={{ color: "#6b21a8", fontSize: '17px', marginLeft: '5px' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Sale Return Details" arrow>
                                                <IconButton
                                                    size="small"
                                                    component={Link}
                                                    to={`/SaleReturnDetails/${data.Id}`}
                                                >
                                                    <Info fontSize="small" style={{ color: "#f97316", fontSize: '17px', marginLeft: '10px' }} />
                                                </IconButton>
                                            </Tooltip>
                                            {hasEditMasterPermission && (
                                                <Tooltip title="Edit Sale Return" arrow>
                                                    <IconButton
                                                        size="small"
                                                        component={Link}
                                                        to={`/SaleReturnEdit/${data.Id}`}
                                                    >
                                                        <EditIcon fontSize="small" style={{ color: "#2563eb", fontSize: '17px', marginLeft: '10px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {hasDeleteMasterPermission && (
                                                <Tooltip title={data.IsActive ? "Delete Sale Return" : "Activate Sale Return"} arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            data.IsActive
                                                                ? openConfirmDialog(data.Id)
                                                                : handleActivate(data.Id)
                                                        }
                                                    >
                                                        {data.IsActive ? (
                                                            <DeleteIcon fontSize="small" style={{ color: "#dc2626", fontSize: '17px', marginLeft: '10px' }} />
                                                        ) : (
                                                            <ActivateIcon
                                                                fontSize="small"
                                                                style={{ color: "#15803d", fontSize: '17px', marginLeft: '10px' }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        style={{ fontSize: "12px" }}
                    />
                </TableContainer>
            </div>
        </div>

    );
}
//------------------------------------------
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export default SaleReturnIndex;

